import { useMemo, useState } from 'react';
import { ALL_AGENTS } from '../constants/dashboardConstants';
import { AgentType } from '../interfaces/Agent';
import { DateRangeDate } from '../interfaces/Analytics';
import { useConversationsTableState } from './useConversationsTableState';

interface Params {
  selectedAgent: string;
  dateRange: [DateRangeDate, DateRangeDate];
  agentType: AgentType;
}

/** Table state shared by both dashboards: chips, page, sort, search and their resets. */
export const useDashboardTableState = ({
  selectedAgent,
  dateRange,
  agentType,
}: Params) => {
  // Selected category chips (empty = all)
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const sortedCategories = useMemo(
    () => [...selectedCategories].sort(),
    [selectedCategories]
  );

  // Server paging only with one agent picked. "All agents" stays unpaged for now.
  const isAllAgents = !selectedAgent || selectedAgent === ALL_AGENTS;
  const table = useConversationsTableState(agentType);

  // Reset during render, not in an effect, so it is one request and not two
  const [seenAgent, setSeenAgent] = useState(selectedAgent);
  if (seenAgent !== selectedAgent) {
    setSeenAgent(selectedAgent);
    if (selectedCategories.length > 0) {
      setSelectedCategories([]);
    }
    if (table.search) {
      table.setSearch('');
    }
    // The sort stays on purpose
  }
  const dataSetKey = [
    selectedAgent,
    dateRange[0]?.format('YYYY-MM-DD'),
    dateRange[1]?.format('YYYY-MM-DD'),
  ].join('|');
  const pageContext = dataSetKey + JSON.stringify(sortedCategories);
  const [pagedContext, setPagedContext] = useState(pageContext);
  if (pagedContext !== pageContext) {
    setPagedContext(pageContext);
    table.backToFirstPage();
  }

  return {
    selectedCategories,
    setSelectedCategories,
    sortedCategories,
    isAllAgents,
    dataSetKey,
    paging: isAllAgents ? undefined : table.paging,
    serverPaging: (rowCount: number, isFetching: boolean) =>
      isAllAgents ? undefined : table.serverPaging(rowCount, isFetching),
  };
};
